import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchBrands, createBrand } from "@/lib/api";
import { formatINR } from "@/lib/format";
import FormDialog from "@/components/FormDialog";

const statusStyle = {
  Active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  Prospect: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  Inactive: "bg-zinc-500/10 text-zinc-400 border-zinc-700",
};

const Brands = () => {
  const [brands, setBrands] = useState(null);

  useEffect(() => {
    fetchBrands().then(setBrands).catch(() => setBrands([]));
  }, []);

  if (brands === null) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="brands-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Brands</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {brands.length} brand partners · {brands.filter((b) => b.status === "Active").length} active
          </p>
        </div>
        <FormDialog
          testid="brand-create"
          title="Add a new brand"
          description="Quick-add a brand. You can fill in spend, contracts and the contact details later."
          submitLabel="Add Brand"
          trigger={
            <Button className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="add-brand-btn">
              + Add Brand
            </Button>
          }
          fields={[
            { key: "name", label: "Brand name", required: true, placeholder: "Plum Goodness", gridCols: 2 },
            { key: "industry", label: "Industry", placeholder: "Skincare", gridCols: 2 },
            {
              key: "status",
              label: "Status",
              type: "select",
              options: ["Active", "Prospect", "Inactive"],
              gridCols: 2,
            },
            { key: "manager", label: "Account Manager", placeholder: "Priya N.", gridCols: 2 },
            { key: "email", label: "Contact email", placeholder: "brand@example.com", gridCols: 2 },
            { key: "phone", label: "Phone", placeholder: "+91 …", gridCols: 2 },
          ]}
          onSubmit={async (form) => {
            const body = {
              name: form.name,
              industry: form.industry || "",
              manager: form.manager || "",
              status: form.status || "Prospect",
              activeCampaigns: 0,
              totalSpend: 0,
              contact: { email: form.email || "", phone: form.phone || "" },
            };
            const created = await createBrand(body);
            setBrands((bs) => [...bs, created]);
            toast.success(`${created.name} added`);
          }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="brands-grid">
        {brands.map((b) => (
          <article key={b.id} className="rounded-xl border border-white/8 bg-white/[0.02] p-5 hover:border-blue-500/30 hover:-translate-y-1 transition-all" data-testid={`brand-card-${b.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="h-11 w-11 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/10 border border-white/8 flex items-center justify-center text-zinc-100 font-semibold">
                  {b.name.charAt(0)}
                </div>
                <h3 className="mt-3 text-lg font-semibold tracking-tight text-zinc-50">{b.name}</h3>
                <p className="text-xs text-zinc-500">{b.industry}</p>
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${statusStyle[b.status] || "bg-zinc-500/10 text-zinc-300 border-zinc-700"}`}>
                {b.status}
              </span>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500">Active</div>
                <div className="text-lg font-semibold mt-0.5">{b.activeCampaigns || 0}</div>
              </div>
              <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500">Spend</div>
                <div className="text-lg font-semibold mt-0.5">{formatINR(b.totalSpend || 0)}</div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
              <span className="text-zinc-500">AM:</span>
              <span className="text-zinc-300">{b.manager}</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default Brands;
